/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Stack, Text, Card, List } from '@/shared/ui';
import { CheckIcon } from '@radix-ui/react-icons';

interface RequestConfirmationProps {
  protocolNumber: string;
  userEmail: string;
  onBackToHome: () => void;
}

export const RequestConfirmation: React.FC<RequestConfirmationProps> = ({
  protocolNumber,
  userEmail,
  onBackToHome,
}) => {
  const { t } = useTranslation('@pages/data-rights');

  return (
    <Stack gap="md">
      <Alert color="green" icon={<CheckIcon />} title={t('dataRights.confirmation.title')}>
        <Text size="sm">{t('dataRights.confirmation.message')}</Text>
      </Alert>

      <Card padding="lg" radius="md" withBorder>
        <Stack gap="md">
          <div>
            <Text size="sm" style={{ color: 'var(--text-secondary)' }} mb={4}>
              {t('dataRights.confirmation.protocolLabel')}
            </Text>
            <Text size="xl" fw={700} style={{ fontFamily: 'monospace' }}>
              {protocolNumber}
            </Text>
          </div>

          <div>
            <Text size="sm" style={{ color: 'var(--text-secondary)' }} mb={4}>
              {t('dataRights.confirmation.emailLabel')}
            </Text>
            <Text fw={500}>{userEmail}</Text>
          </div>
        </Stack>
      </Card>

      <Card padding="lg" radius="md" withBorder className="bg-(--surface-secondary)">
        <Text size="md" fw={600} mb="md">
          {t('dataRights.confirmation.nextStepsTitle')}
        </Text>
        <List spacing="sm">
          <List.Item>{t('dataRights.confirmation.step1')}</List.Item>
          <List.Item>{t('dataRights.confirmation.step2')}</List.Item>
          <List.Item>{t('dataRights.confirmation.step3')}</List.Item>
        </List>
      </Card>

      <Alert color="blue">
        <Text size="sm" fw={500} mb="xs">
          {t('dataRights.confirmation.responseTimeTitle')}
        </Text>
        <Text size="sm">{t('dataRights.confirmation.responseTimeMessage')}</Text>
      </Alert>

      <Text size="xs" className="text-(--text-secondary)">
        {t('dataRights.confirmation.contactInfo')}
      </Text>

      <Button fullWidth onClick={onBackToHome} mt="md">
        {t('dataRights.confirmation.backToSettings')}
      </Button>
    </Stack>
  );
};
